import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useAppState } from '../contexts/AppStateContext';
import api from '../utils/axiosConfig';

const CreateEvent = () => {
  const navigate = useNavigate();
  const { currentOrganization, addNewEvent,refreshData } = useAppState();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [customDomains, setCustomDomains] = useState([]);

  const [event, setEvent] = useState({
    name: '',
    organization: currentOrganization?.id || null,
    auto_approve: false,
    is_paid: false,
    custom_domain: '',
    date: '',
    registration_start_date: '',
    registration_end_date: '',
    logo: null,
    landing_page_template: null,
    ticket_image: null,
    submission_approved_email: '',
    submission_rejected_email: '',
    submission_received_email: '',
    event_reminder_email: '',
    allow_multiple_tickets_purchase: false,
    max_tickets_per_customer: ''
  });

  const [logoPreview, setLogoPreview] = useState(null);
  const [ticketImagePreview, setTicketImagePreview] = useState(null);
  const [fileData, setFileData] = useState({
    logo: null,
    ticket_image: null
  });

  
  // Update organization ID if currentOrganization changes
  useEffect(() => {
    if (currentOrganization?.id) {
      setEvent(prev => ({ ...prev, organization: currentOrganization.id }));
    }
  }, [currentOrganization]);

  // Fetch custom domains on component mount
  useEffect(() => {
    const fetchCustomDomains = async () => {
      try {
        const response = await api.get(`/organizations/${currentOrganization.id}/custom-domains/`);
        setCustomDomains(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching custom domains:', err);
        // Only set error for custom domains, not for the entire form
        // We'll display this error only near the custom domain field
      }
    };

    if (currentOrganization?.id) {
      fetchCustomDomains();
    }
  }, [currentOrganization]);

  // Effect to auto-enable auto_approve when is_paid is true
  useEffect(() => {
    if (event.is_paid && !event.auto_approve) {
      setEvent(prev => ({ ...prev, auto_approve: true }));
    }
  }, [event.is_paid]);

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    if (type === 'checkbox') {
      if (name === 'is_paid') {
        // If is_paid is being checked, also set auto_approve to true
        if (checked) {
          setEvent(prev => ({
            ...prev,
            [name]: checked,
            auto_approve: true
          }));
        } else {
          // If is_paid is being unchecked, just update is_paid normally
          setEvent(prev => ({ ...prev, [name]: checked }));
        }
      } else if (name === 'auto_approve') {
        // If trying to uncheck auto_approve while is_paid is true, prevent it
        if (event.is_paid && !checked) {
          // Don't allow unchecking auto_approve when is_paid is true
          return;
        }
        // Otherwise update normally
        setEvent(prev => ({ ...prev, [name]: checked }));
      } else {
        // Handle other checkboxes normally
        setEvent(prev => ({ ...prev, [name]: checked }));
      }
    } else if (type === 'file') {
      // Store file objects separately
      if (files[0]) {
        setFileData(prev => ({ ...prev, [name]: files[0] }));

        // Create preview for image files
        if (name === 'logo') {
          const newLogoURL = URL.createObjectURL(files[0]);
          setLogoPreview(newLogoURL);
        } else if (name === 'ticket_image') {
          const newTicketURL = URL.createObjectURL(files[0]);
          setTicketImagePreview(newTicketURL);
        }
      }
    } else {
      setEvent(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!currentOrganization?.id) {
      setError('No organization selected. Please select an organization first.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Create a FormData object for sending files
      const formData = new FormData();

      // Add all regular form fields to FormData
      Object.keys(event).forEach(key => {
        // Skip file fields as we'll handle them separately
        if (key !== 'logo' && key !== 'ticket_image' && event[key] !== null) {
          // Handle custom_domain specially - only include if not empty
          if (key === 'custom_domain') {
            if (event[key] && event[key] !== '') {
              formData.append(key, event[key]);
              console.log(`Adding custom_domain: ${event[key]}`);
            }
          } else {
            formData.append(key, event[key]);
          }
        }
      });

      // Add file fields from fileData if they exist
      if (fileData.logo) {
        formData.append('logo', fileData.logo);
      }

      if (fileData.ticket_image) {
        formData.append('ticket_image', fileData.ticket_image);
      }

      if (event.is_paid && !formData.has('auto_approve')) {
        formData.append('auto_approve', 'true');
      }

      const res = await api.post(
        `/organizations/${currentOrganization.id}/events/`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      console.log('Event created successfully:', res.data);
      await refreshData();

      navigate(`/?org=${currentOrganization.id}&event=${res.data.id}&tab=event-details`);

    } catch (err) {
      console.error('Error creating event:', err);
      setError(err.response?.data?.message || 'Failed to create event. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Clean up object URLs when component unmounts
  useEffect(() => {
    return () => {
      if (logoPreview) URL.revokeObjectURL(logoPreview);
      if (ticketImagePreview) URL.revokeObjectURL(ticketImagePreview);
    };
  }, [logoPreview, ticketImagePreview]);

  // Form groups as specified
  const formGroups = [
    {
      title: "General Info",
      fields: ["name", "custom_domain", "date", "logo", "ticket_image"],
    },
    {
      title: "Payment and Registration",
      fields: [
        "auto_approve",
        "is_paid",
        "registration_start_date",
        "registration_end_date",
        "allow_multiple_tickets_purchase",
        "max_tickets_per_customer",
      ],
    },
    {
      title: "Communication",
      fields: [
        "submission_approved_email",
        "submission_rejected_email",
        "submission_received_email",
        "event_reminder_email",
      ],
    }
  ];

  // Render form field based on field name
  const renderField = (fieldName) => {
    switch (fieldName) {
      case 'name':
        return (
          <div className="mb-4">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">Event Name <span className="text-red-500 ml-0.5" aria-hidden="true">*</span></label>
            <input
              type="text"
              id="name"
              name="name"
              value={event.name}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              required
            />
          </div>
        );

      case 'custom_domain':
        return (
          <div className="mb-4">
            <label htmlFor="custom_domain" className="block text-sm font-medium text-gray-700">Custom Domain</label>
            <select
              id="custom_domain"
              name="custom_domain"
              value={event.custom_domain}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            >
              <option value="">None</option>
              {customDomains.map(domain => (
                <option key={domain.id} value={domain.id}>
                  {domain.domain || domain.domain_name}
                </option>
              ))}
            </select>
            {customDomains.length === 0 && (
              <p className="text-gray-500 text-sm mt-1">
                No custom domains available. Please create one in the organization settings.
              </p>
            )}
          </div>
        );

      case 'date':
        return (
          <div className="mb-4">
            <label htmlFor="date" className="block text-sm font-medium text-gray-700">Event Date <span className="text-red-500 ml-0.5" aria-hidden="true">*</span></label>
            <input
              type="datetime-local"
              id="date"
              name="date"
              value={event.date}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              required
            />
          </div>
        );

      case 'logo':
        return (
          <div className="mb-4">
            <label htmlFor="logo" className="block text-sm font-medium text-gray-700">Event Logo</label>
            <input
              type="file"
              id="logo"
              name="logo"
              onChange={handleChange}
              className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
              accept="image/*"
            />
            {logoPreview && (
              <div className="mt-2">
                <img
                  src={logoPreview}
                  alt="Logo preview"
                  className="h-20 w-auto object-contain"
                />
              </div>
            )}
          </div>
        );

      case 'ticket_image':
        return (
          <div className="mb-4">
            <label htmlFor="ticket_image" className="block text-sm font-medium text-gray-700">Ticket Image</label>
            <input
              type="file"
              id="ticket_image"
              name="ticket_image"
              onChange={handleChange}
              className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
              accept="image/*"
            />
            {ticketImagePreview && (
              <div className="mt-2">
                <img
                  src={ticketImagePreview}
                  alt="Ticket preview"
                  className="h-20 w-auto object-contain"
                />
              </div>
            )}
          </div>
        );
      case 'auto_approve':
        return (
          <div className="mb-4 flex items-start">
            <div className="flex h-5 items-center">
              <input
                type="checkbox"
                id="auto_approve"
                name="auto_approve"
                checked={event.auto_approve}
                onChange={handleChange}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                disabled={event.is_paid} // Disable when is_paid is true
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor="auto_approve" className="font-medium text-gray-700">
                Auto Approve Registrations
                {event.is_paid && (
                  <span className="ml-2 text-xs italic text-indigo-600">
                    (Required for paid events)
                  </span>
                )}
              </label>
            </div>
          </div>
        );

      case 'is_paid':
        return (
          <div className="mb-4 flex items-start">
            <div className="flex h-5 items-center">
              <input
                type="checkbox"
                id="is_paid"
                name="is_paid"
                checked={event.is_paid}
                onChange={handleChange}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor="is_paid" className="font-medium text-gray-700">
                Paid Event
                <span className="ml-2 text-xs italic text-gray-500">
                  (Will automatically enable auto-approval)
                </span>
              </label>
            </div>
          </div>
        );

      case 'registration_start_date':
        return (
          <div className="mb-4">
            <label htmlFor="registration_start_date" className="block text-sm font-medium text-gray-700">Registration Start Date <span className="text-red-500 ml-0.5" aria-hidden="true">*</span></label>
            <input
              type="datetime-local"
              id="registration_start_date"
              name="registration_start_date"
              value={event.registration_start_date}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              required
            />
          </div>
        );

      case 'registration_end_date':
        return (
          <div className="mb-4">
            <label htmlFor="registration_end_date" className="block text-sm font-medium text-gray-700">Registration End Date <span className="text-red-500 ml-0.5" aria-hidden="true">*</span></label>
            <input
              type="datetime-local"
              id="registration_end_date"
              name="registration_end_date"
              value={event.registration_end_date}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              required
            />
          </div>
        );

      case 'allow_multiple_tickets_purchase':
        return (
          <div className="mb-4 flex items-start">
            <div className="flex h-5 items-center">
              <input
                type="checkbox"
                id="allow_multiple_tickets_purchase"
                name="allow_multiple_tickets_purchase"
                checked={event.allow_multiple_tickets_purchase}
                onChange={handleChange}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor="allow_multiple_tickets_purchase" className="font-medium text-gray-700">Allow Multiple Tickets Purchase</label>
            </div>
          </div>
        );

      case 'max_tickets_per_customer':
        return (
          <div className="mb-4">
            <label htmlFor="max_tickets_per_customer" className="block text-sm font-medium text-gray-700">
              Max Tickets Per Customer
              <span className="ml-2 text-xs italic text-gray-500">
                (Counted by email. Leave blank for no limit)
              </span>
            </label>
            <input
              type="number"
              min="1"
              step="1"
              id="max_tickets_per_customer"
              name="max_tickets_per_customer"
              value={event.max_tickets_per_customer ?? ''}
              onChange={handleChange}
              placeholder="Unlimited"
              className="mt-1 block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>
        );

      case 'submission_approved_email':
      case 'submission_rejected_email':
      case 'submission_received_email':
      case 'event_reminder_email':
        const emailLabels = {
          'submission_approved_email': 'Submission Approved Email Template',
          'submission_rejected_email': 'Submission Rejected Email Template',
          'submission_received_email': 'Submission Received Email Template',
          'event_reminder_email': 'Event Reminder Email Template'
        };

        return (
          <div className="mb-4">
            <label htmlFor={fieldName} className="block text-sm font-medium text-gray-700">{emailLabels[fieldName]}</label>
            <textarea
              id={fieldName}
              name={fieldName}
              value={event[fieldName]}
              onChange={handleChange}
              rows={4}
              className="mt-1 block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (

    <div className="bg-white p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Create New Event</h2>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <p className="mb-4 text-sm text-gray-500">
          Fields marked with <span className="text-red-500" aria-hidden="true">*</span> are required.
        </p>
        {formGroups.map((group, groupIndex) => (
          <div key={groupIndex} className="collapse bg-base-100 border-gray-500 border mb-6">
            <input type="checkbox" defaultChecked />
            <div className="collapse-title font-semibold">{group.title}</div>
            <div className="collapse-content text-sm">
              <div className="mb-8">
                <h3 className="text-lg font-medium text-gray-900 mb-4 pb-2 border-b">{group.title}</h3>
                <div className="space-y-4">
                  {group.fields.map(field => renderField(field))}
                </div>
              </div>
            </div>
          </div>
        ))}

        <div className="flex justify-end mt-6">
          <button
            type="button"
            className="mr-3 px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            onClick={() => navigate(-1)}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating...
              </span>
            ) : (
              'Create Event'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateEvent;
